// Vite entry point
// Import CSS - Vite will bundle it
import "./styles.css";

// Register the <dotlottie-wc> custom element. Replaces the previous
// <dotlottie-player> CDN script (which was deprecated) and unblocks
// offline / PWA / Capacitor WebView use.
import "@lottiefiles/dotlottie-wc";

// Capacitor native integration (no-op in browser)
import { initCapacitor } from "./capacitor-init.ts";

const CAPACITOR_HTTP_TIMER_PREFIX = "CapacitorHttp fetch ";

type CapacitorBridgeWindow = Window & typeof globalThis & {
    Capacitor?: {
        isLoggingEnabled?: boolean;
        logToNative?: (...args: unknown[]) => void;
        logFromNative?: (...args: unknown[]) => void;
    };
};

function silenceCapacitorBridgeLogging(): void {
    const cap = (window as CapacitorBridgeWindow).Capacitor;
    if (!cap) {
        return;
    }

    cap.isLoggingEnabled = false;
    cap.logToNative = () => undefined;
    cap.logFromNative = () => undefined;
}

function silenceCapacitorHttpTimerSpam(): void {
    const originalTime = console.time?.bind(console);
    const originalTimeEnd = console.timeEnd?.bind(console);
    const originalTimeLog = console.timeLog?.bind(console);
    if (!originalTime || !originalTimeEnd) {
        return;
    }

    console.time = (label?: string) => {
        if (typeof label === "string" && label.startsWith(CAPACITOR_HTTP_TIMER_PREFIX)) {
            return;
        }
        originalTime(label);
    };

    console.timeEnd = (label?: string) => {
        if (typeof label === "string" && label.startsWith(CAPACITOR_HTTP_TIMER_PREFIX)) {
            return;
        }
        originalTimeEnd(label);
    };

    if (originalTimeLog) {
        console.timeLog = (label?: string, ...data: unknown[]) => {
            if (typeof label === "string" && label.startsWith(CAPACITOR_HTTP_TIMER_PREFIX)) {
                return;
            }
            originalTimeLog(label, ...data);
        };
    }
}

silenceCapacitorBridgeLogging();
silenceCapacitorHttpTimerSpam();

// Apply the saved theme synchronously, before the app shell renders,
// to avoid a light/dark flash. Runs even before initCapacitor so it works
// on first paint in both web and Capacitor contexts.
import { initTheme } from "./theme.ts";
initTheme();

// Initialize Capacitor first (sets up server URL, fetch patching),
// then load the app. This is an async IIFE to avoid top-level await
// which some WebViews don't support.
(async () => {
    await initCapacitor();
    // Dynamic import so init.ts runs AFTER Capacitor has patched fetch.
    // The legacy generic SyncManager is intentionally not started: the app's
    // authoritative sync path is state.ts + /drive/*, while SyncManager uses
    // a separate Dexie/Redis data model that is not consumed by the UI.
    await import("./init.ts");
})();
